/* ==========================================================================
   Asian American Forward Website - Main JavaScript
   ========================================================================== */

// Import modules (using ES6 modules with Vite)
import { initializeGSAPAnimations } from './utils/gsap-animations.js';
import { initializeAnimationManager } from './utils/animation-manager.js';
import { initializeLoading } from './loading.js';
import { initializeNavigation } from './navigation.js';
import { initializeHeroCards } from './hero-cards.js';
import { initializeDiagonalSeparator } from './diagonal-separator.js';
import { initializeCardModal } from './card-modal.js';
import { initializeEventModal } from './event-modal.js';
import { initializeAboutImage } from './about-image.js';
import { initializeMissionImage } from './mission-image.js';
import { initializeFounderImage } from './founder-image.js';
import { initializeTimeline } from './timeline.js';
import { initializeNewsletter } from './newsletter.js';
import { initializeSmoothScroll } from './smooth-scroll.js';
import { initializeScrollingLines } from './scrolling-lines.js'; // Re-enabled scrolling lines
import { initializeTeamMemberImages } from './team-member-image.js';
import { runAnimationTests } from './test-animations.js';

/* ==========================================================================
   Application Initialization
   ========================================================================== */

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', function() {
  console.log('Asian American Forward Website - Initializing...');
  
  try {
    // Initialize loading screen first to ensure content is properly hidden
    initializeLoading();
    
    // Try GSAP animations first (most reliable for Windows animation bypass)
    console.log('🎬 Attempting to initialize GSAP...');
    const gsapSuccess = initializeGSAPAnimations();
    
    if (gsapSuccess) {
      console.log('✅ GSAP successfully initialized');
      // Add visual indicator to body
      document.body.setAttribute('data-animation-engine', 'gsap');
      // Initialize hero animations immediately if GSAP is active
      window.dispatchEvent(new CustomEvent('gsapLoadedAndReady'));
    } else {
      console.log('❌ GSAP failed to initialize, falling back to animation manager');
      // Fallback to animation manager if GSAP not available
      initializeAnimationManager();
      document.body.setAttribute('data-animation-engine', 'css');
    }
    
    // Initialize navigation functionality
    initializeNavigation();
    
    // Mobile menu animations disabled - using pure CSS for Windows compatibility
    // setupMobileMenuAnimation();
    
    // Initialize hero cards system
    initializeHeroCards();
    
    // Initialize About Us image functionality
    initializeAboutImage();
    
    // Initialize Mission image functionality
    initializeMissionImage();
    
    // Initialize Founder image functionality (About page)
    initializeFounderImage();
    
    // Initialize Team Member image functionality (About page)
    initializeTeamMemberImages();
    
    // Initialize Timeline functionality (About page)
    initializeTimeline();
    
    // Diagonal separator is now handled by GSAP in setupTextTicker()
    // initializeDiagonalSeparator();
    
    // Initialize card modal system
    initializeCardModal();
    
    // Initialize event modal system
    initializeEventModal();
    
    // Initialize newsletter form
    initializeNewsletter();
    
    // Initialize smooth scrolling
    initializeSmoothScroll();
    
    // Initialize scrolling diagonal lines - re-enabled
    initializeScrollingLines();
    
    console.log('✅ All features initialized successfully');
    
    // Debug: Check if donate button animation is working
    setTimeout(() => {
      const donateButton = document.querySelector('.nav-link--primary');
      if (donateButton) {
        const computedStyle = window.getComputedStyle(donateButton);
        console.log('🎨 Donate button border color:', computedStyle.borderColor);
        console.log('🎨 Donate button animation:', computedStyle.animation);
      }
    }, 3000); // Check after 3 seconds
    
    // Run animation tests in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      setTimeout(() => {
        runAnimationTests();
      }, 1000);
    }
    
  } catch (error) {
    console.error('❌ Error initializing website features:', error);
  }
});

/* ==========================================================================
   Global Error Handling
   ========================================================================== */

// Handle any unhandled promise rejections
window.addEventListener('unhandledrejection', function(event) {
  console.error('Unhandled promise rejection:', event.reason);
  event.preventDefault();
});

// Handle global errors
window.addEventListener('error', function(event) {
  console.error('Global error:', event.error);
});

/* ==========================================================================
   Performance Monitoring
   ========================================================================== */

// Monitor page load performance
window.addEventListener('load', function() {
  if ('performance' in window) {
    const perfData = performance.timing;
    const loadTime = perfData.loadEventEnd - perfData.navigationStart;
    console.log(`📊 Page loaded in ${loadTime}ms`);
    
    // Optional: Send performance data to analytics
    // sendPerformanceData(loadTime);
  }
});

/* ==========================================================================
   Reduced Motion Support
   ========================================================================== */

// Check for reduced motion preference and apply global settings
function checkReducedMotion() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    document.body.classList.add('reduced-motion');
    console.log('🔄 Reduced motion mode enabled');
  }
  
  // Listen for changes in motion preference
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', function(e) {
    if (e.matches) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
  });
}

// Initialize reduced motion check
checkReducedMotion();

/* ==========================================================================
   Utility Functions
   ========================================================================== */

// Debounce function for performance optimization
export function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

// Throttle function for scroll events
export function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Check if element is in viewport
export function isInViewport(element, threshold = 0) {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  
  return (
    rect.top <= windowHeight * (1 + threshold) &&
    rect.bottom >= windowHeight * -threshold &&
    rect.left <= windowWidth * (1 + threshold) &&
    rect.right >= windowWidth * -threshold
  );
}

// Smooth animation frame helper
export function animate(duration, easing, callback) {
  const start = performance.now();
  
  function frame(time) {
    const elapsed = time - start;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing ? easing(progress) : progress;
    
    callback(easedProgress);
    
    if (progress < 1) {
      requestAnimationFrame(frame);
    }
  }
  
  requestAnimationFrame(frame);
}

// Easing functions
export const easings = {
  easeOutCubic: t => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeOutQuart: t => 1 - Math.pow(1 - t, 4),
  easeInOutQuart: t => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2
}; 